import { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Descriptions, Empty, Flex, Space, Spin, Table, Tag, Tooltip, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { describePod } from "@/apis/access";
import { useFormatter } from "@/hooks/useFormatter";

// Access tab "Summary": a `kubectl describe pod` style view of the selected server, served by
// GET /servers/{ns}/{pod}?op=describe. It works for a pod that is not running, which is when it
// matters: the issues list on top says why (not scheduled, image pull, crash loop, probe ...).

const { Text } = Typography;

// waiting reasons that are normal while a container starts
const STARTING = ["ContainerCreating", "PodInitializing"];

// how long ago, the way kubectl prints it: 45s, 12m, 5h, 3d
const age = (value) => {
  if (!value) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  if (s < 120) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 120) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const allReady = (ready) => {
  const [n, total] = (ready || "").split("/");
  return n === total && total !== "0";
};

const podColor = (desc) => {
  const status = desc.status || "";
  if (status === "Running") return allReady(desc.ready) ? "success" : "warning";
  if (status === "Completed" || status === "Succeeded") return "default";
  if (status === "Pending" || status === "Terminating" || STARTING.includes(status) || /^Init:\d+\/\d+$/.test(status)) return "processing";
  return "error";
};

const containerColor = (c) => {
  if (c.state === "Running") return c.ready || (c.init && !c.sidecar) ? "success" : "warning";
  if (c.state === "Waiting") return STARTING.includes(c.stateReason) ? "processing" : "error";
  if (c.state === "Terminated") return c.exitCode === 0 ? "default" : "error";
  return "default";
};

const alertType = (level) => (level === "error" ? "error" : level === "warning" ? "warning" : "info");

const kv = (obj) => Object.entries(obj || {}).map(([k, v]) => `${k} ${v}`).join(", ");

const Tags = ({ values }) =>
  values.length ? <Space size={[4, 4]} wrap>{values.map((v) => <Tag key={v}>{v}</Tag>)}</Space> : "-";

const Wrap = ({ children }) => <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{children}</span>;

export const PodSummary = ({ namespace, podname, refreshKey }) => {
  const { formatDateTime } = useFormatter();
  const [desc, setDesc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);
  const seq = useRef(0);

  const load = async () => {
    const mine = ++seq.current;
    setLoading(true);
    const response = await describePod(namespace, podname);
    if (mine !== seq.current) return; // a newer load is in flight
    setLoading(false);
    if (response.status === "OK" && Array.isArray(response.value?.containers)) {
      setDesc(response.value);
      setError(null);
    } else if (response.status === "OK") {
      // a provision service from before op=describe answers unknown ops with {status: OK, value: {}}
      setError("The provision service does not support the server summary yet. Redeploy tenant-provision.");
    } else {
      setError(response.message || response.error?.message || "Failed to describe the server");
    }
    setLoadedAt(new Date());
  };

  useEffect(() => {
    if (podname) load();
  }, [namespace, podname, refreshKey]);

  const when = (value) => (value ? `${formatDateTime(value)} (${age(value)} ago)` : "-");

  if (!podname) return <Empty description="Select a server" />;
  if (!desc) {
    if (error) return <Alert type="error" showIcon message={`Cannot describe ${podname}`} description={<Wrap>{error}</Wrap>} />;
    return <Flex justify="center" style={{ padding: 40 }}><Spin /></Flex>;
  }

  const issues = desc.issues || [];
  const events = desc.events || [];
  const volumes = desc.volumes || [];
  const healthy = desc.phase === "Running" && allReady(desc.ready) && !desc.deletedAt
                  && !issues.some((i) => i.level !== "info");

  const overview = [
    { key: "namespace", label: "Namespace", children: desc.namespace },
    { key: "node", label: "Node", children: desc.node || <Text type="secondary">not scheduled</Text> },
    { key: "controlledBy", label: "Controlled by", children: desc.controlledBy || "-" },
    { key: "podIP", label: "Pod IP", children: desc.podIP || "-" },
    { key: "hostIP", label: "Host IP", children: desc.hostIP || "-" },
    { key: "qos", label: "QoS class", children: desc.qosClass || "-" },
    { key: "created", label: "Created", children: when(desc.createdAt) },
    { key: "started", label: "Started", children: when(desc.startTime) },
    { key: "sa", label: "Service account", children: desc.serviceAccount || "-" },
  ];
  // a full row each: these run long (the 9 above fill exactly 3 rows of 3)
  if (desc.priorityClass) overview.push({ key: "priority", label: "Priority class", span: 3, children: desc.priorityClass });
  if (Object.keys(desc.nodeSelector || {}).length) {
    overview.push({ key: "nodeSelector", label: "Node selector", span: 3,
                    children: <Tags values={Object.entries(desc.nodeSelector).map(([k, v]) => `${k}=${v}`)} /> });
  }
  overview.push({ key: "labels", label: "Labels", span: 3,
                  children: <Tags values={Object.entries(desc.labels || {}).map(([k, v]) => `${k}=${v}`)} /> });
  if ((desc.tolerations || []).length) {
    overview.push({ key: "tolerations", label: "Tolerations", span: 3, children: <Tags values={desc.tolerations} /> });
  }

  // [one per row, grid]: image/state/probes run long, the rest are short
  const containerItems = (c) => {
    const items = [
      { key: "image", label: "Image", children: <Text copyable style={{ wordBreak: "break-all" }}>{c.image}</Text> },
      { key: "state", label: "State", children: (
        <Space direction="vertical" size={0}>
          <Space size="small" wrap>
            <Tag color={containerColor(c)}>{c.stateReason ? `${c.state}: ${c.stateReason}` : c.state === "NotCreated" ? "Not created" : c.state}</Tag>
            {c.exitCode !== null && c.exitCode !== undefined && <Text type="secondary">exit code {c.exitCode}</Text>}
            {c.startedAt && <Text type="secondary">started {when(c.startedAt)}</Text>}
            {c.finishedAt && <Text type="secondary">finished {when(c.finishedAt)}</Text>}
          </Space>
          {c.stateMessage && <Wrap>{c.stateMessage}</Wrap>}
        </Space>
      ) },
    ];
    if (c.lastState) {
      const last = c.lastState;
      items.push({ key: "last", label: "Last state", children: (
        <Space direction="vertical" size={0}>
          <Space size="small" wrap>
            <Tag color={last.reason === "OOMKilled" || last.exitCode ? "error" : "default"}>Terminated{last.reason ? `: ${last.reason}` : ""}</Tag>
            {last.exitCode !== null && last.exitCode !== undefined && <Text type="secondary">exit code {last.exitCode}</Text>}
            {last.finishedAt && <Text type="secondary">finished {when(last.finishedAt)}</Text>}
          </Space>
          {last.message && <Wrap>{last.message}</Wrap>}
        </Space>
      ) });
    }
    const grid = [
      { key: "ready", label: "Ready", children: c.ready ? "True" : "False" },
      { key: "restarts", label: "Restarts", children: c.restartCount },
      { key: "ports", label: "Ports", children: c.ports?.length ? c.ports.join(", ") : "-" },
      { key: "requests", label: "Requests", children: kv(c.requests) || "-" },
      { key: "limits", label: "Limits", children: kv(c.limits) || "-" },
    ];
    const probes = [];
    if (c.liveness) probes.push({ key: "liveness", label: "Liveness", children: c.liveness });
    if (c.readiness) probes.push({ key: "readiness", label: "Readiness", children: c.readiness });
    if (c.startup) probes.push({ key: "startup", label: "Startup", children: c.startup });
    if (c.mounts?.length) probes.push({ key: "mounts", label: "Mounts", children: <Tags values={c.mounts} /> });
    return [items, grid, probes];
  };

  const conditionColumns = [
    { title: "Type", dataIndex: "type", key: "type", width: 200 },
    { title: "Status", dataIndex: "status", key: "status", width: 80,
      render: (v) => <Tag color={v === "True" ? "success" : v === "False" ? "error" : "default"}>{v}</Tag> },
    { title: "Reason", dataIndex: "reason", key: "reason", width: 180 },
    { title: "Message", dataIndex: "message", key: "message", render: (v) => <Wrap>{v}</Wrap> },
    { title: "Since", dataIndex: "lastTransitionTime", key: "since", width: 80,
      render: (v) => v && <Tooltip title={formatDateTime(v)}>{age(v)}</Tooltip> },
  ];

  const eventColumns = [
    { title: "Type", dataIndex: "type", key: "type", width: 90,
      render: (v) => <Tag color={v === "Warning" ? "warning" : "default"}>{v}</Tag> },
    { title: "Reason", dataIndex: "reason", key: "reason", width: 150 },
    { title: "Age", dataIndex: "lastTime", key: "age", width: 70,
      render: (v, e) => <Tooltip title={`first ${formatDateTime(e.firstTime)}, last ${formatDateTime(v)}`}>{age(v)}</Tooltip> },
    { title: "Count", dataIndex: "count", key: "count", width: 70, render: (v) => (v > 1 ? `x${v}` : "") },
    { title: "From", dataIndex: "source", key: "source", width: 150 },
    { title: "Message", dataIndex: "message", key: "message", render: (v) => <Wrap>{v}</Wrap> },
  ];

  const volumeColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Type", dataIndex: "type", key: "type" },
    { title: "Source", dataIndex: "source", key: "source", render: (v) => v || "-" },
  ];

  return (
    <Flex vertical gap={12} style={{ padding: "8px 4px" }}>
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Space size="middle" wrap>
          <Text strong>{desc.name}</Text>
          <Tag color={podColor(desc)}>{desc.status}</Tag>
          <Text type="secondary">Ready {desc.ready}</Text>
          <Text type="secondary">Restarts {desc.restarts}</Text>
          <Text type="secondary">Age {age(desc.createdAt)}</Text>
        </Space>
        <Space size="small">
          {loadedAt && <Text type="secondary">Updated {formatDateTime(loadedAt)}</Text>}
          <Tooltip title="Refresh">
            <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={load} />
          </Tooltip>
        </Space>
      </Flex>

      {error && <Alert type="error" showIcon message="Refresh failed, showing the previous result" description={<Wrap>{error}</Wrap>} />}

      <Flex vertical gap={6}>
        {healthy && <Alert type="success" showIcon message="Running and ready" />}
        {issues.map((issue, idx) => (
          <Alert key={idx} type={alertType(issue.level)} showIcon
                 message={<>{issue.title}{issue.time && <Text type="secondary"> &middot; {when(issue.time)}</Text>}</>}
                 description={issue.detail ? <Wrap>{issue.detail}</Wrap> : undefined} />
        ))}
      </Flex>

      <Descriptions size="small" bordered column={3} items={overview} />

      {desc.containers.map((c) => {
        const [top, grid, bottom] = containerItems(c);
        return (
          <Card key={`${c.init ? "init" : "main"}/${c.name}`} size="small"
                title={<Space size="small">{c.sidecar ? "Sidecar container" : c.init ? "Init container" : "Container"} <Text strong>{c.name}</Text></Space>}>
            <Descriptions size="small" column={1} items={top} />
            <Descriptions size="small" column={{ xs: 1, md: 2, xl: 3 }} items={grid} />
            {bottom.length > 0 && <Descriptions size="small" column={1} items={bottom} />}
          </Card>
        );
      })}

      <Card size="small" title="Conditions">
        <Table size="small" rowKey="type" pagination={false} dataSource={desc.conditions || []} columns={conditionColumns}
               locale={{ emptyText: "No conditions reported yet" }} />
      </Card>

      <Card size="small" title={`Events (${events.length})`}>
        {desc.eventsAvailable ? (
          <Table size="small" dataSource={events.map((e, i) => ({ ...e, key: i }))} columns={eventColumns}
                 pagination={events.length > 10 ? { pageSize: 10, size: "small" } : false}
                 locale={{ emptyText: "No events. Kubernetes keeps events for about an hour." }} />
        ) : (
          <Alert type="warning" showIcon message="Events could not be read for this server" />
        )}
      </Card>

      {volumes.length > 0 && (
        <Card size="small" title="Volumes">
          <Table size="small" rowKey="name" pagination={false} dataSource={volumes} columns={volumeColumns} />
        </Card>
      )}
    </Flex>
  );
};
